/**
 * Textures.js — the FX texture foundry.
 * owner: fx
 *
 * EVERY particle / decal texture in the game is rendered here, on the GPU, by a
 * fragment shader into a WebGLRenderTarget at boot. Nothing is downloaded and
 * nothing is authored on the CPU pixel by pixel.
 *
 * Atlases are 4x4 grids. Channel packing is uniform across both atlases so the
 * particle and decal shaders can share intuition:
 *
 *   SPRITE atlas   R = height field (billow thickness, used for normals + self-shadow)
 *                  G = detail noise  (used to erode/dissolve the particle as it dies)
 *                  B = hot-core mask (emissive core for sparks, embers, fire)
 *                  A = coverage
 *
 *   DECAL atlas    R = height  (0.5 = flush with the wall, <0.5 = crater, >0.5 = lip)
 *                  G = luminance modulation, 0.5 = untouched, 0 = black, 1 = 2x
 *                      (so a chipped rim can be BRIGHTER than the weathered
 *                       surface around it — fresh material, not just a stain)
 *                  B = tint mask (dust ring, blood, soot — multiplied by the decal tint)
 *                  A = coverage
 */
import * as THREE from 'three';
import { FullScreenQuad } from 'three/examples/jsm/postprocessing/Pass.js';
import { NOISE } from './Shaders.js';

/* Sprite atlas tile indices (row-major, 4 per row). */
export const SPRITE = {
  smoke: 0, wisp: 1, dust: 2, sparkStreak: 3,
  sparkPoint: 4, ember: 5, chip: 6, splinter: 7,
  shard: 8, droplet: 9, blood: 10, leaf: 11,
  star: 12, ring: 13, fire: 14, mist: 15,
};

/* Decal atlas tile indices. */
export const DECAL_TILE = {
  holeConcrete: 0, holeMetal: 1, holeWood: 2, holeDirt: 3,
  glassShatter: 4, bloodA: 5, bloodB: 6, scorch: 7,
  waterRipple: 8, foliage: 9, holeGlass: 10, dustSmudge: 11,
  crackWeb: 12, scuff: 13, sandCrater: 14, splashRing: 15,
};

const ATLAS_HEADER = /* glsl */`
precision highp float;
varying vec2 vUv;
${NOISE}

/* signed distance to a rounded n-gon, used for angular debris silhouettes */
float sdPoly(vec2 p, float n, float r, float rot){
  float an = 6.28318530718 / n;
  float a = mod(atan(p.y, p.x) + rot + 3.14159265, an) - an * 0.5;
  return cos(a) * length(p) - r;
}

/* one soft anisotropic spike through the origin */
float spike(vec2 p, float ang, float len, float w){
  vec2 q = rot2(-ang) * p;
  float d = sat(1.0 - abs(q.x) / len);
  return pow(d, 2.5) * exp(-abs(q.y) / w);
}
`;

/* ------------------------------------------------------------------ *
 * SPRITE ATLAS
 * ------------------------------------------------------------------ */
const SPRITE_ATLAS_FS = /* glsl */`
${ATLAS_HEADER}

vec4 tileSmoke(vec2 uv, float seed){
  vec2 c = uv * 2.0 - 1.0;
  // low frequency lumps deform the disc so no two puffs read as a circle
  float lump = fbm2(uv * 3.1 + seed * 19.0, 4, 0.55);
  float r = length(c * (1.0 + vec2(lump - 0.5, 0.5 - lump) * 0.55));
  float mask = smoothstep(1.04, 0.05, r);
  float n = warpFbm(uv * 4.2 + seed * 7.0, 6);
  // A long soft shoulder instead of a hard threshold. With a hard edge every
  // puff resolves as a separate cotton ball with a visible boundary; the whole
  // point of a smoke sprite is that fifty of them integrate into one volume, and
  // that only happens if each one has no edge of its own.
  float a = sat(mask * 1.42 - (1.0 - n) * 0.88);
  a = smoothstep(0.0, 0.86, a);
  a *= mix(0.55, 1.0, smoothstep(1.0, 0.38, r));
  // billow height: cauliflower lobes, densest where the noise is dense
  float lobes = 0.30 + 0.70 * pow(sat(n * 1.25), 1.3);
  float h = a * lobes * smoothstep(1.02, 0.20, r);
  float detail = fbm2(uv * 9.0 + seed * 3.0, 5, 0.55);
  return vec4(h, detail, 0.0, a);
}

vec4 tileWisp(vec2 uv){
  vec2 c = uv * 2.0 - 1.0;
  c.x *= 2.3;                                  // thin, drawn out
  float r = length(c);
  float n = warpFbm(uv * vec2(9.0, 3.5) + 31.0, 6);
  float mask = smoothstep(1.05, 0.1, r);
  float a = sat(mask * 1.45 - (1.0 - n) * 1.05);
  a = smoothstep(0.0, 0.4, a);
  float h = a * (0.3 + 0.7 * n);
  return vec4(h, fbm2(uv * 12.0, 4, 0.5), 0.0, a);
}

vec4 tileDust(vec2 uv){
  vec2 c = uv * 2.0 - 1.0;
  // Dust is airborne powder, not a torn cloth: the density falls off smoothly
  // from the core so a hundred of them integrate into ONE cloud instead of
  // reading as a hundred separate flakes. All the break-up comes from the
  // per-particle dissolve over life, not from a hard-edged mask.
  float lump = fbm2(uv * 2.4 + 47.0, 4, 0.6);
  float r = length(c * (1.0 + vec2(lump - 0.5, 0.45 - lump) * 0.55));
  float n = warpFbm(uv * 2.6 + 3.7, 5);
  float soft = pow(sat(1.05 - r), 1.85);
  float a = sat(soft * (0.52 + 0.78 * n));
  float h = a * (0.30 + 0.70 * n) * smoothstep(1.02, 0.22, r);
  return vec4(h, n, 0.0, a);
}

vec4 tileSparkStreak(vec2 uv){
  vec2 c = uv * 2.0 - 1.0;
  float t = sat(c.y * 0.5 + 0.5);              // 0 = tail, 1 = head
  float wobble = (fbm2(vec2(c.y * 2.4, 5.1), 3, 0.5) - 0.5) * 0.10 * (1.0 - t);
  float x = abs(c.x - wobble);
  float thick = mix(0.020, 0.085, t * t);
  float body = smoothstep(thick * 3.2, 0.0, x);
  float along = smoothstep(1.0, 0.86, abs(c.y)) * mix(0.25, 1.0, pow(t, 0.6));
  float a = body * along;
  float core = smoothstep(thick * 1.1, 0.0, x) * along;
  core += smoothstep(0.16, 0.0, length(vec2(c.x - wobble, (c.y - 0.86) * 1.6))) * 1.2;
  return vec4(a, 1.0, sat(core), sat(a + core * 0.6));
}

vec4 tileSparkPoint(vec2 uv){
  vec2 c = uv * 2.0 - 1.0;
  float r = length(c);
  float g = exp(-r * r * 16.0);
  float star = spike(c, 0.0, 0.95, 0.035) + spike(c, 1.5708, 0.75, 0.03);
  float a = sat(g * 1.6 + star * 0.55);
  return vec4(a, 1.0, sat(g * 2.4 + star * 0.5), a);
}

vec4 tileEmber(vec2 uv){
  vec2 c = uv * 2.0 - 1.0;
  vec3 w = worley2(uv * 3.0 + 9.0);
  float r = length(c) * (0.85 + 0.4 * w.z);
  float shape = smoothstep(0.62, 0.12, r);
  float crust = smoothstep(0.35, 0.75, w.x);   // dark crust between hot cracks
  float hot = shape * (1.0 - crust * 0.85);
  float glow = exp(-r * r * 5.0) * 0.55;
  float a = sat(shape + glow * 0.6);
  return vec4(shape, w.x, sat(hot * 1.3 + glow), a);
}

vec4 tileChip(vec2 uv){
  vec2 c = uv * 2.0 - 1.0;
  float d = sdPoly(c, 5.0, 0.60, 0.7);
  float warp = (fbm2(uv * 6.0 + 21.0, 4, 0.5) - 0.5) * 0.24;
  float a = smoothstep(0.03, -0.03, d + warp);
  // faceted height: two planes meeting along a ridge -> a real specular break
  float facet = abs(dot(normalize(c + 1e-4), vec2(0.6, 0.8)));
  float h = a * (0.35 + 0.65 * facet) * (0.7 + 0.5 * fbm2(uv * 9.0, 3, 0.5));
  return vec4(h, fbm2(uv * 14.0, 3, 0.5), 0.0, a);
}

vec4 tileSplinter(vec2 uv){
  vec2 c = uv * 2.0 - 1.0;
  float taper = 1.0 - pow(abs(c.y), 1.7);
  float bend = sin(c.y * 2.1) * 0.10;
  float x = abs(c.x - bend);
  float a = smoothstep(0.13 * taper, 0.0, x) * step(0.0, taper);
  float grain = fbm2(vec2(c.x * 12.0, c.y * 2.0), 4, 0.5);
  float h = a * (0.35 + 0.65 * grain);
  return vec4(h, grain, 0.0, a);
}

vec4 tileShard(vec2 uv){
  vec2 c = uv * 2.0 - 1.0;
  float d = sdPoly(c * vec2(1.0, 0.72), 3.0, 0.62, 0.35);
  float a = smoothstep(0.02, -0.02, d);
  // two flat facets -> glass catches light in hard slivers, never a soft blob
  float f = step(0.0, dot(c, vec2(0.83, 0.55)));
  float h = a * mix(0.25, 0.95, f);
  float edge = smoothstep(0.10, 0.0, abs(d)) * a;
  return vec4(h, edge, sat(edge * 1.5), a);
}

vec4 tileDroplet(vec2 uv){
  vec2 c = uv * 2.0 - 1.0;
  c.y *= 0.82;
  float tail = smoothstep(0.0, 1.0, sat(-c.y));
  float r = length(vec2(c.x / (1.0 - tail * 0.55), c.y));
  float a = smoothstep(0.58, 0.42, r);
  float h = a * sqrt(sat(1.0 - r / 0.58));
  float spec = smoothstep(0.20, 0.0, length(c - vec2(-0.16, 0.19))) * a;
  return vec4(h, 1.0, sat(spec * 1.4), a);
}

vec4 tileBlood(vec2 uv){
  vec2 c = uv * 2.0 - 1.0;
  float ang = atan(c.y, c.x);
  float wob = fbm2(vec2(cos(ang), sin(ang)) * 2.2 + 13.0, 4, 0.55);
  float r = length(c) / (0.42 + 0.30 * wob);
  float a = smoothstep(1.05, 0.85, r);
  // satellite droplet — blood never lands as one clean circle
  a = max(a, smoothstep(0.13, 0.09, length(c - vec2(0.42, 0.36))));
  a = max(a, smoothstep(0.08, 0.05, length(c - vec2(-0.38, 0.44))));
  float h = a * sqrt(sat(1.0 - r * 0.85));
  return vec4(h, wob, 0.0, a);
}

vec4 tileLeaf(vec2 uv){
  vec2 c = uv * 2.0 - 1.0;
  c = rot2(0.5) * c;
  float body = 1.0 - abs(c.x) / (0.46 * (1.0 - c.y * c.y * 0.9) + 1e-3);
  float a = smoothstep(0.0, 0.10, body) * step(abs(c.y), 0.98);
  float rib = smoothstep(0.035, 0.0, abs(c.x)) * a;
  float veins = smoothstep(0.55, 0.9, fbm2(vec2(c.x * 5.0, c.y * 14.0), 3, 0.5)) * a;
  float h = a * (0.45 + 0.35 * rib + 0.2 * veins);
  return vec4(h, veins, 0.0, a);
}

vec4 tileStar(vec2 uv){
  vec2 c = uv * 2.0 - 1.0;
  float r = length(c);
  float core = exp(-r * r * 26.0);
  float s = 0.0;
  s += spike(c, 0.0, 1.0, 0.030) * 1.0;
  s += spike(c, 1.5708, 0.62, 0.026) * 0.62;
  s += spike(c, 0.7854, 0.36, 0.020) * 0.30;
  s += spike(c, -0.7854, 0.36, 0.020) * 0.30;
  float halo = exp(-r * 3.4) * 0.30;
  float a = sat(core * 1.8 + s + halo);
  return vec4(a, 1.0, sat(core * 2.6 + s * 1.2), a);
}

vec4 tileRing(vec2 uv){
  vec2 c = uv * 2.0 - 1.0;
  float ang = atan(c.y, c.x);
  float wob = (fbm2(vec2(cos(ang), sin(ang)) * 3.0 + 5.0, 4, 0.5) - 0.5) * 0.06;
  float r = length(c) + wob;
  float ring = smoothstep(0.30, 0.72, r) * smoothstep(0.96, 0.74, r);
  float a = sat(ring * 1.3);
  return vec4(a * 0.5, 1.0, a, a);
}

vec4 tileFire(vec2 uv){
  vec2 c = uv * 2.0 - 1.0;
  float lump = fbm2(uv * 2.6 + 41.0, 4, 0.6);
  float r = length(c * (1.0 + vec2(lump - 0.5, 0.4 - lump) * 0.5));
  // Pure ridged noise gives every flame sprite the same crescent silhouette, and
  // a fireball made of thirty identical crescents reads as a decal sheet. Mixing
  // in plain fbm keeps the filament structure while breaking the repeat.
  float n = mix(fbm2(uv * 3.2 + 7.0, 5, 0.55), ridged2(uv * 4.0 + 2.0, 5), 0.45);
  float mask = smoothstep(1.0, 0.05, r);
  float a = sat(mask * 1.35 - (1.0 - n) * 0.72);
  a = smoothstep(0.0, 0.48, a);
  float hot = sat(pow(smoothstep(0.85, 0.0, r), 1.6) * (0.5 + 0.9 * n));
  return vec4(a * (0.4 + 0.6 * n), n, hot, a);
}

vec4 tileMist(vec2 uv){
  vec2 c = uv * 2.0 - 1.0;
  float r = length(c);
  float n = fbm2(uv * 3.4 + 61.0, 5, 0.6);
  float a = smoothstep(1.0, 0.0, r) * (0.35 + 0.65 * n);
  a = sat(a * 0.75);
  return vec4(a, n, 0.0, a);
}

void main(){
  vec2 g = vUv * 4.0;
  vec2 t = floor(g);
  vec2 uv = fract(g);
  int idx = int(t.y) * 4 + int(t.x);
  vec4 o = vec4(0.0);
  if      (idx == 0)  o = tileSmoke(uv, 0.0);
  else if (idx == 1)  o = tileWisp(uv);
  else if (idx == 2)  o = tileDust(uv);
  else if (idx == 3)  o = tileSparkStreak(uv);
  else if (idx == 4)  o = tileSparkPoint(uv);
  else if (idx == 5)  o = tileEmber(uv);
  else if (idx == 6)  o = tileChip(uv);
  else if (idx == 7)  o = tileSplinter(uv);
  else if (idx == 8)  o = tileShard(uv);
  else if (idx == 9)  o = tileDroplet(uv);
  else if (idx == 10) o = tileBlood(uv);
  else if (idx == 11) o = tileLeaf(uv);
  else if (idx == 12) o = tileStar(uv);
  else if (idx == 13) o = tileRing(uv);
  else if (idx == 14) o = tileFire(uv);
  else                o = tileMist(uv);

  // hard-clip a 2px guard band so mip generation cannot bleed between tiles
  vec2 e = min(uv, 1.0 - uv);
  float guard = smoothstep(0.0, 0.012, min(e.x, e.y));
  o *= guard;
  gl_FragColor = clamp(o, 0.0, 1.0);
}
`;

/* ------------------------------------------------------------------ *
 * DECAL ATLAS
 * ------------------------------------------------------------------ */
const DECAL_ATLAS_FS = /* glsl */`
${ATLAS_HEADER}

/* radial cracks radiating from the impact, thinning outward */
float radialCracks(vec2 c, float count, float len, float seedv){
  float ang = atan(c.y, c.x);
  float r = length(c);
  float a = mod(ang + 3.14159265, 6.28318530718 / count) * count - 3.14159265;
  float jitter = (hash11(floor((ang + 3.14159265) / (6.28318530718 / count)) + seedv) - 0.5) * 0.9;
  float wob = (fbm2(vec2(r * 9.0, seedv * 13.0), 4, 0.5) - 0.5) * 0.35;
  float d = abs(a + jitter * 0.35 + wob) * r;
  float w = mix(0.020, 0.002, sat(r / len));
  return smoothstep(w, 0.0, d) * smoothstep(len, len * 0.15, r) * step(0.06, r);
}

vec4 holeBase(vec2 uv, float coreR, float rimR, float dustR, float rough, float seedv){
  vec2 c = uv * 2.0 - 1.0;
  float ang = atan(c.y, c.x);
  float edge = fbm2(vec2(cos(ang), sin(ang)) * 3.5 + seedv, 5, 0.55);
  float r = length(c) * (1.0 + (edge - 0.5) * rough);

  float hole = smoothstep(coreR, coreR * 0.55, r);
  float rim  = smoothstep(rimR, coreR * 0.8, r) - hole;
  float dust = smoothstep(dustR, coreR, r) * (0.35 + 0.65 * fbm2(uv * 9.0 + seedv, 5, 0.55));

  // height: sunken bore, raised lip of displaced material around it
  float h = 0.5 - hole * 0.5 + rim * 0.18 * (0.4 + 0.6 * edge);
  // luminance: dark bore (never black), and freshly exposed material on the
  // chipped lip that reads BRIGHTER than the weathered surface
  float occ = 0.5 - hole * 0.38 - rim * 0.10 * (0.5 + 0.5 * edge)
            + rim * 0.24 * smoothstep(0.35, 0.75, edge);
  float cov = sat(smoothstep(dustR * 1.05, dustR * 0.25, r) * 0.9 + hole + rim);
  return vec4(h, sat(occ), sat(dust), sat(cov));
}

vec4 tileHoleConcrete(vec2 uv){
  vec2 c = uv * 2.0 - 1.0;
  vec4 b = holeBase(uv, 0.20, 0.42, 0.92, 0.42, 3.0);
  float cracks = radialCracks(c, 9.0, 0.72, 3.0);
  // chipped spall — chunks knocked out of the surface around the bore
  float spall = smoothstep(0.55, 0.85, worley2(uv * 6.0 + 2.0).x) * smoothstep(0.62, 0.22, length(c));
  b.x = mix(b.x, 0.36, cracks * 0.9);
  b.x = mix(b.x, 0.40, spall * 0.7);
  b.y = sat(b.y - cracks * 0.26 + spall * 0.20);
  b.z = sat(b.z + spall * 0.45);
  b.w = sat(b.w + cracks * 0.8 + spall * 0.5);
  return b;
}

vec4 tileHoleMetal(vec2 uv){
  vec2 c = uv * 2.0 - 1.0;
  vec4 b = holeBase(uv, 0.17, 0.33, 0.58, 0.20, 11.0);
  // petalled tear-out and bright scored metal around the entry
  float petals = sat(cos(atan(c.y, c.x) * 7.0) * 0.5 + 0.5);
  float lip = smoothstep(0.40, 0.17, length(c)) * petals;
  b.x = sat(b.x + lip * 0.30);
  b.y = sat(b.y + lip * 0.34);            // scraped metal is BRIGHTER than the paint
  float scratches = smoothstep(0.72, 0.98, fbm2(vec2(atan(c.y, c.x) * 6.0, length(c) * 22.0), 3, 0.5));
  b.y = sat(b.y + scratches * smoothstep(0.75, 0.2, length(c)) * 0.40);
  b.z *= 0.35;
  b.w = sat(b.w + lip * 0.6);
  return b;
}

vec4 tileHoleWood(vec2 uv){
  vec2 c = uv * 2.0 - 1.0;
  vec4 b = holeBase(uv, 0.19, 0.44, 0.80, 0.55, 23.0);
  // torn fibres pulled out along the grain
  float grain = fbm2(vec2(uv.x * 3.0, uv.y * 26.0), 4, 0.5);
  float fibre = smoothstep(0.6, 0.95, grain) * smoothstep(0.75, 0.20, length(c));
  b.x = sat(b.x + fibre * 0.22);
  b.y = sat(b.y + fibre * 0.16);          // torn pale fibres catch the light
  b.z = sat(b.z * 0.6 + fibre * 0.5);
  b.w = sat(b.w + fibre * 0.7);
  return b;
}

vec4 tileHoleDirt(vec2 uv){
  vec2 c = uv * 2.0 - 1.0;
  float r = length(c);
  float n = fbm2(uv * 5.0 + 7.0, 5, 0.55);
  float crater = smoothstep(0.52, 0.05, r * (1.0 + (n - 0.5) * 0.5));
  float ejecta = smoothstep(0.95, 0.45, r) * (0.3 + 0.7 * n) * (1.0 - crater);
  float h = 0.5 - crater * 0.34 + ejecta * 0.18;
  float occ = 0.5 - crater * 0.24 + ejecta * 0.06;
  return vec4(h, sat(occ), sat(ejecta * 0.9 + crater * 0.2), sat(crater + ejecta * 0.85));
}

vec4 tileGlassShatter(vec2 uv){
  vec2 c = uv * 2.0 - 1.0;
  float r = length(c);
  float radial = radialCracks(c, 13.0, 0.98, 5.0);
  // concentric rings joining the radials, like real spiderwebbing
  float rings = 0.0;
  for (int i = 1; i <= 4; i++){
    float rr = 0.16 * float(i) + 0.06 * hash11(float(i) * 3.7);
    float wob = (fbm2(vec2(atan(c.y, c.x) * 2.5, float(i)), 3, 0.5) - 0.5) * 0.05;
    rings += smoothstep(0.012, 0.0, abs(r + wob - rr));
  }
  float hole = smoothstep(0.11, 0.05, r);
  float crush = smoothstep(0.24, 0.09, r) * (0.4 + 0.6 * worley2(uv * 14.0).x);
  float cr = sat(radial + rings * smoothstep(0.98, 0.2, r) * 0.85);
  float h = 0.5 - hole * 0.45 - cr * 0.10;
  float occ = 0.5 - hole * 0.32 + cr * 0.30 + crush * 0.18;   // cracks catch light
  return vec4(h, sat(occ), sat(cr * 0.8 + crush), sat(cr + hole + crush * 0.8));
}

vec4 bloodSplat(vec2 uv, float seedv, float spread){
  vec2 c = uv * 2.0 - 1.0;
  float ang = atan(c.y, c.x);
  float wob = fbm2(vec2(cos(ang), sin(ang)) * 2.6 + seedv, 5, 0.6);
  float pool = smoothstep(1.0, 0.86, length(c) / (0.26 + 0.24 * wob));
  // directional cast-off droplets
  float drops = 0.0;
  for (int i = 0; i < 26; i++){
    float fi = float(i);
    vec2 h2 = hash22(vec2(fi, seedv));
    float a2 = (h2.x - 0.5) * 2.4 + 1.2;
    float d2 = 0.22 + h2.y * spread;
    vec2 p = vec2(cos(a2), sin(a2)) * d2;
    float s = mix(0.015, 0.075, hash11(fi * 2.3 + seedv));
    float e = smoothstep(s, s * 0.5, length(c - p));
    // little streak trailing back toward the centre
    vec2 dir = normalize(p + 1e-4);
    float tail = smoothstep(s * 0.6, 0.0, abs(dot(c - p, vec2(-dir.y, dir.x))))
               * smoothstep(s * 4.0, 0.0, max(0.0, dot(c - p, dir)));
    drops = max(drops, max(e, tail * 0.7));
  }
  float a = sat(pool + drops);
  float h = 0.5 + a * 0.05;
  return vec4(h, sat(0.5 - a * 0.24), a, a);
}

vec4 tileScorch(vec2 uv){
  vec2 c = uv * 2.0 - 1.0;
  float ang = atan(c.y, c.x);
  float wob = fbm2(vec2(cos(ang), sin(ang)) * 2.2 + 71.0, 5, 0.6);
  float r = length(c) / (0.55 + 0.35 * wob);
  float soot = smoothstep(1.05, 0.1, r) * (0.4 + 0.6 * fbm2(uv * 6.0 + 3.0, 5, 0.6));
  float core = smoothstep(0.45, 0.0, r);
  return vec4(0.5, sat(0.5 - soot * 0.34 - core * 0.09), sat(soot * 0.5), sat(soot * 1.2));
}

vec4 tileWaterRipple(vec2 uv){
  vec2 c = uv * 2.0 - 1.0;
  float r = length(c);
  float w = sin(r * 26.0) * 0.5 + 0.5;
  float env = smoothstep(0.95, 0.15, r) * smoothstep(0.0, 0.18, r);
  float h = 0.5 + (w - 0.5) * 0.35 * env;
  return vec4(h, sat(0.5 - env * 0.05), env * 0.25, env);
}

vec4 tileFoliage(vec2 uv){
  vec2 c = uv * 2.0 - 1.0;
  float n = fbm2(uv * 4.5 + 51.0, 5, 0.6);
  float smear = smoothstep(0.9, 0.15, length(c * vec2(1.0, 1.6))) * (0.3 + 0.7 * n);
  return vec4(0.5, sat(0.5 - smear * 0.20), smear, sat(smear * 1.1));
}

vec4 tileHoleGlass(vec2 uv){
  vec2 c = uv * 2.0 - 1.0;
  float r = length(c);
  float cr = radialCracks(c, 8.0, 0.55, 9.0);
  float hole = smoothstep(0.10, 0.04, r);
  float crush = smoothstep(0.19, 0.07, r) * worley2(uv * 18.0).x;
  return vec4(0.5 - hole * 0.4, sat(0.5 - hole * 0.30 + cr * 0.22 + crush * 0.14),
              sat(cr * 0.7 + crush), sat(cr + hole + crush * 0.7));
}

vec4 tileDustSmudge(vec2 uv){
  vec2 c = uv * 2.0 - 1.0;
  float n = fbm2(uv * 4.0 + 91.0, 5, 0.6);
  float m = smoothstep(1.0, 0.05, length(c)) * (0.25 + 0.75 * n);
  return vec4(0.5, sat(0.5 + m * 0.06), m, sat(m * 0.9));
}

vec4 tileCrackWeb(vec2 uv){
  vec2 c = uv * 2.0 - 1.0;
  vec3 w = worley2(uv * 4.0 + 33.0);
  float cracks = smoothstep(0.055, 0.0, w.y - w.x) * smoothstep(1.0, 0.15, length(c));
  cracks = max(cracks, radialCracks(c, 6.0, 0.8, 13.0));
  return vec4(0.5 - cracks * 0.13, sat(0.5 - cracks * 0.24), cracks * 0.4, sat(cracks * 1.1));
}

vec4 tileScuff(vec2 uv){
  vec2 c = uv * 2.0 - 1.0;
  float streak = smoothstep(0.55, 0.95, fbm2(vec2(uv.x * 30.0, uv.y * 2.0), 4, 0.5));
  float env = smoothstep(1.0, 0.2, length(c * vec2(0.8, 1.7)));
  float m = streak * env;
  return vec4(0.5 + m * 0.06, sat(0.5 + m * 0.26), m * 0.2, sat(m));
}

vec4 tileSandCrater(vec2 uv){
  vec2 c = uv * 2.0 - 1.0;
  float n = fbm2(uv * 6.0 + 17.0, 5, 0.55);
  float r = length(c) * (1.0 + (n - 0.5) * 0.35);
  float crater = smoothstep(0.46, 0.0, r);
  float lip = smoothstep(0.30, 0.52, r) * smoothstep(0.86, 0.5, r);
  float ejecta = smoothstep(1.0, 0.5, r) * n;
  return vec4(0.5 - crater * 0.28 + lip * 0.20, sat(0.5 - crater * 0.16 + lip * 0.14),
              sat(lip * 0.8 + ejecta * 0.5), sat(crater + lip + ejecta * 0.7));
}

vec4 tileSplashRing(vec2 uv){
  vec2 c = uv * 2.0 - 1.0;
  float r = length(c);
  float ring = smoothstep(0.45, 0.80, r) * smoothstep(1.0, 0.82, r);
  float n = fbm2(uv * 8.0 + 5.0, 4, 0.5);
  float m = ring * (0.4 + 0.6 * n);
  return vec4(0.5 + m * 0.05, sat(0.5 - m * 0.11), m, sat(m));
}

void main(){
  vec2 g = vUv * 4.0;
  vec2 t = floor(g);
  vec2 uv = fract(g);
  int idx = int(t.y) * 4 + int(t.x);
  vec4 o = vec4(0.5, 0.5, 0.0, 0.0);
  if      (idx == 0)  o = tileHoleConcrete(uv);
  else if (idx == 1)  o = tileHoleMetal(uv);
  else if (idx == 2)  o = tileHoleWood(uv);
  else if (idx == 3)  o = tileHoleDirt(uv);
  else if (idx == 4)  o = tileGlassShatter(uv);
  else if (idx == 5)  o = bloodSplat(uv, 2.0, 0.62);
  else if (idx == 6)  o = bloodSplat(uv, 19.0, 0.78);
  else if (idx == 7)  o = tileScorch(uv);
  else if (idx == 8)  o = tileWaterRipple(uv);
  else if (idx == 9)  o = tileFoliage(uv);
  else if (idx == 10) o = tileHoleGlass(uv);
  else if (idx == 11) o = tileDustSmudge(uv);
  else if (idx == 12) o = tileCrackWeb(uv);
  else if (idx == 13) o = tileScuff(uv);
  else if (idx == 14) o = tileSandCrater(uv);
  else                o = tileSplashRing(uv);

  vec2 e = min(uv, 1.0 - uv);
  float guard = smoothstep(0.0, 0.012, min(e.x, e.y));
  o.w *= guard;
  o.z *= guard;
  o.x = mix(0.5, o.x, guard);
  o.y = mix(0.5, o.y, guard);
  gl_FragColor = clamp(o, 0.0, 1.0);
}
`;

/* ------------------------------------------------------------------ *
 * CURL FIELD — a tileable, divergence-free vector field baked once.
 * ------------------------------------------------------------------ */
const CURL_FS = /* glsl */`
precision highp float;
varying vec2 vUv;
${NOISE}

float pnoise(vec2 p, float period){
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  vec2 i0 = mod(i, period), i1 = mod(i + 1.0, period);
  float a = hash12(vec2(i0.x, i0.y)), b = hash12(vec2(i1.x, i0.y));
  float c = hash12(vec2(i0.x, i1.y)), d = hash12(vec2(i1.x, i1.y));
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}

float pfbm(vec2 p, float period, int oct){
  float a = 0.5, s = 0.0, n = 0.0, per = period;
  for (int i = 0; i < 5; i++){
    if (i >= oct) break;
    s += a * pnoise(p, per); n += a; a *= 0.55; p *= 2.0; per *= 2.0;
  }
  return s / max(n, 1e-4);
}

void main(){
  float PER = 8.0;
  vec2 p = vUv * PER;
  float e = 1.0 / 256.0 * PER;
  // two independent scalar potentials -> a divergence-free 2D curl + a swirl term
  float pa = pfbm(p, PER, 4);
  float dx = pfbm(p + vec2(e, 0.0), PER, 4) - pfbm(p - vec2(e, 0.0), PER, 4);
  float dy = pfbm(p + vec2(0.0, e), PER, 4) - pfbm(p - vec2(0.0, e), PER, 4);
  vec2 curl = vec2(dy, -dx) / (2.0 * e);
  float pb = pfbm(p + 37.0, PER, 4);
  vec3 v = normalize(vec3(curl, (pb - 0.5) * 2.0) + 1e-5);
  gl_FragColor = vec4(v * 0.5 + 0.5, pa);
}
`;

/* ------------------------------------------------------------------ *
 * Fallback blue-ish noise (used only if ctx.materials.blueNoise is absent)
 * ------------------------------------------------------------------ */
const BLUENOISE_FS = /* glsl */`
precision highp float;
varying vec2 vUv;
${NOISE}
void main(){
  // high-pass a white-noise field: cheap approximation of void-and-cluster
  vec2 p = vUv * 256.0;
  float c = hash12(floor(p));
  float lo = 0.0;
  for (int y = -2; y <= 2; y++)
    for (int x = -2; x <= 2; x++)
      lo += hash12(mod(floor(p) + vec2(float(x), float(y)), 256.0));
  lo /= 25.0;
  float v = fract(0.5 + (c - lo) * 1.9);
  gl_FragColor = vec4(v, fract(v + 0.3819), fract(v + 0.7639), 1.0);
}
`;

/* ------------------------------------------------------------------ *
 * factory
 * ------------------------------------------------------------------ */
function renderToTarget(renderer, fragmentShader, size, opts = {}) {
  const rt = new THREE.WebGLRenderTarget(size, size, {
    type: opts.type ?? THREE.UnsignedByteType,
    format: THREE.RGBAFormat,
    minFilter: opts.mips === false ? THREE.LinearFilter : THREE.LinearMipmapLinearFilter,
    magFilter: THREE.LinearFilter,
    wrapS: opts.wrap ?? THREE.ClampToEdgeWrapping,
    wrapT: opts.wrap ?? THREE.ClampToEdgeWrapping,
    generateMipmaps: opts.mips !== false,
    depthBuffer: false,
    stencilBuffer: false,
    colorSpace: THREE.NoColorSpace,
  });
  rt.texture.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy());
  const quad = new FullScreenQuad(new THREE.ShaderMaterial({
    vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }',
    fragmentShader,
    depthTest: false, depthWrite: false,
  }));
  const prevTarget = renderer.getRenderTarget();
  const prevAuto = renderer.autoClear;
  renderer.autoClear = true;
  renderer.setRenderTarget(rt);
  quad.render(renderer);
  renderer.setRenderTarget(prevTarget);
  renderer.autoClear = prevAuto;
  quad.material.dispose();
  quad.dispose();
  return rt;
}

export function buildFXTextures(renderer, quality = 'ultra') {
  const atlasSize = quality === 'low' ? 1024 : 2048;
  const sprites = renderToTarget(renderer, SPRITE_ATLAS_FS, atlasSize);
  const decals = renderToTarget(renderer, DECAL_ATLAS_FS, atlasSize);
  const curl = renderToTarget(renderer, CURL_FS, 256, {
    wrap: THREE.RepeatWrapping, mips: false,
  });
  const blue = renderToTarget(renderer, BLUENOISE_FS, 256, {
    wrap: THREE.RepeatWrapping, mips: false,
  });
  return {
    sprites: sprites.texture,
    decals: decals.texture,
    curl: curl.texture,
    blueNoise: blue.texture,
    _targets: [sprites, decals, curl, blue],
    dispose() { for (const t of this._targets) t.dispose(); },
  };
}
